/**
 * GestorPermisos — Pantalla de configuración de permisos finos.
 *
 * Acceso: solo Super Administrador (forzado por requireSuperAdmin
 * en el backend; el frontend solo exige sesión).
 *
 * Estructura:
 * - Árbol-navegador (izquierda): construido a partir de las claves
 *   con notación de puntos (modulo.recurso.accion). Hojas con varias
 *   acciones colapsan en un toggle Ver/Gestionar/Borrar/etc.
 * - Panel de detalle (derecha):
 *   - Roles con acceso (pills) — Super Administrador siempre activo.
 *   - Usuarios con dos pestañas: Con acceso / Sin acceso. Cada usuario
 *     muestra el origen de su acceso (rol, individual, ambos, super-admin).
 *
 * Datos: GET /api/permisos/configuracion devuelve los 35 permisos
 * activos con sus asignaciones de rol y persona en una sola llamada.
 * Persistencia: PUT /api/permisos/:clave (full-replace de roles+usuarios).
 *
 * Caso especial: nodo "Inventario · Acceso por tipo" abre
 * <MatrizAccesoTipos /> en lugar del panel estándar; usa otro
 * subsistema (typeAccessService).
 */

import React, { useState, useEffect, useMemo } from "react";
import {
    fetchConfiguracionPermisos,
    fetchUsuariosAsignables,
    fetchUnidadesNegocioFiltro,
    fetchDepartamentosFiltro,
    updatePermiso
} from '../../peticiones_API/PeticionesPermisos';
import { toast } from 'react-toastify';
import {
    Search, Save, Users,
    X, Loader2, Building, Network,
    ChevronRight, ChevronDown, Folder, File, Settings2,
    ShieldCheck, UserCheck, UserPlus, Lock, Check, Plus,
} from "lucide-react";

// DS v2
import {
    Button, Input, Select,
    Card, CardContent, Badge,
    Avatar, cn,
} from './ui';

import MatrizAccesoTipos from './MatrizAccesoTipos';
import PanelAmbitoVacaciones from './PanelAmbitoVacaciones';

const VALOR_TODOS = "__ALL__";
const CLAVE_MATRIZ_TIPOS = "__inv_type_access__";
const CLAVE_AMBITO_VAC = "__vac_ambito__";

// Nodos ESPECIALES del árbol: paneles (NO claves de `permisos`) que se inyectan como primer hijo de
// un módulo y abren su propio componente en el panel derecho. Generaliza el antiguo caso único de
// inventario — con dos consumidores la abstracción ya se ha ganado el sitio.
//   bajo   = key del módulo padre bajo el que cuelga.
//   gate   = (opcional) clave de permiso que debe existir en el catálogo para mostrar el nodo
//            (cosmética + defensa en profundidad; la barrera real son los endpoints).
//   render = (ctx) => JSX; ctx trae { usuarios, unidadesNegocio, departamentos } del gestor.
const NODOS_ESPECIALES = {
    [CLAVE_MATRIZ_TIPOS]: {
        label: 'Acceso por tipo', bajo: 'inventario',
        render: (ctx) => <MatrizAccesoTipos usuarios={ctx.usuarios} unidadesNegocio={ctx.unidadesNegocio} departamentos={ctx.departamentos} />,
    },
    [CLAVE_AMBITO_VAC]: {
        label: 'Ámbito por empresa', bajo: 'vacaciones', gate: 'vacaciones.ambito.configurar',
        render: (ctx) => <PanelAmbitoVacaciones usuarios={ctx.usuarios} />,
    },
};

// Roles asignables (espejo del backend ROLES_DISPONIBLES; el endpoint
// /configuracion no los devuelve, los necesita el card "Roles con acceso").
// Fase D: fuera 'Operativa'. No existe en la tabla `roles` de ningun entorno, asi que esta
// pantalla ofrecia asignar permisos a un rol fantasma. Se comprobo antes de quitarlo: cero filas
// con ese nombre en `permiso_roles` de local, TEST y produccion. La lista gemela del servidor esta
// en `controllers/Permisos_Controller.js` y se cambio a la vez — si dejan de coincidir, el
// servidor filtra lo que esta pantalla ofrece.
const ROLES_DISPONIBLES = [
    'Super Administrador',
    'Administrador',
    'Gestor de Contenidos',
    'Recursos Humanos',
    'General',
];

// Etiquetas en español por segmento de clave + acciones (local, no exportado).
const ETIQUETAS = {
    formacion: 'Formación',
    intervenciones: 'Intervenciones Técnicas',
    inventario: 'Inventario',
    soporte: 'Soporte',
    vacaciones: 'Vacaciones',
    ambito: 'Ámbito',
    configurar: 'Configurar',
    estadisticas: 'Estadísticas',
    lae: 'LAE',
    sepe: 'SEPE',
    catalogo: 'Catálogo',
    proveedores: 'Proveedores',
    cursos: 'Cursos',
    categorias: 'Categorías',
    alumnos: 'Alumnos potenciales',
    areas: 'Áreas',
    convocatorias: 'Convocatorias',
    campanas: 'Campañas',
    dashboard: 'Dashboard',
    informes: 'Informes',
    miscitas: 'Mis citas',
    tecnicos: 'Técnicos',
    alertas: 'Alertas y umbrales',
    eventos: 'Eventos',
    'stock-entries': 'Compras / Stock',
    tipos: 'Tipos de activo',
    panel: 'Panel de gestión',
    ringover: 'RingOver',
    view: 'Ver',
    manage: 'Gestionar',
    delete: 'Borrar',
    enviar: 'Enviar',
    ver_todos: 'Ver todos',
};
const etiqueta = (seg) => ETIQUETAS[seg] ?? seg;

const PRIO_ACCIONES = ['view', 'manage', 'delete', 'enviar', 'ver_todos'];
const ordenarAcciones = (arr) =>
    [...arr].sort((a, b) => ((PRIO_ACCIONES.indexOf(a) + 1 || 99) - (PRIO_ACCIONES.indexOf(b) + 1 || 99)) || a.localeCompare(b));
const ordenarHijos = (arr) => [...arr].sort((a, b) => a.label.localeCompare(b.label, 'es'));

const esNodoEspecial = (clave) => clave != null && Object.prototype.hasOwnProperty.call(NODOS_ESPECIALES, clave);

// Origen del acceso de un usuario a la clave seleccionada (en vivo, contra los
// Sets pendientes de guardar).
// 'super_admin' | 'rol' | 'individual' | 'ambos' | 'implicito_gestion' | null
// `manageRolesSet`/`manageUsuariosSet` (opcionales): asignaciones CRUDAS de la
// clave `.manage` hermana, para reflejar la jerarquía manage⇒view del backend.
// Es SOLO presentación: no se escribe en los Sets que se guardan.
function calcularOrigenAcceso(usuario, rolesSet, usuariosSet, manageRolesSet, manageUsuariosSet) {
    if (usuario.rol === 'Super Administrador') return 'super_admin';
    const porRol = rolesSet.has(usuario.rol);
    const porIndividual = usuariosSet.has(usuario.Id);
    if (porRol && porIndividual) return 'ambos';
    if (porRol) return 'rol';
    if (porIndividual) return 'individual';
    // Acceso a esta `view` derivado de tener la `manage` hermana (no concedido
    // por nadie de forma explícita sobre la view).
    if (manageRolesSet && manageUsuariosSet &&
        (manageRolesSet.has(usuario.rol) || manageUsuariosSet.has(usuario.Id))) {
        return 'implicito_gestion';
    }
    return null;
}
const tieneAcceso = (origen) => origen !== null;

// ── Construcción del árbol desde el payload de /configuracion ──
function construirArbol(configuracion) {
    const countByClave = {};
    for (const p of configuracion) {
        countByClave[p.clave] = (p.roles?.length || 0) + (p.usuarios?.length || 0);
    }

    // path (hoja) → acciones
    const hojas = new Map();
    for (const p of configuracion) {
        const parts = p.clave.split('.');
        if (parts.length === 1) {
            hojas.set(p.clave, new Set()); // plana (ringover): sin acción
        } else {
            const accion = parts[parts.length - 1];
            const path = parts.slice(0, -1).join('.');
            if (!hojas.has(path)) hojas.set(path, new Set());
            hojas.get(path).add(accion);
        }
    }

    // Árbol anidado de nodos crudos
    const rootMap = {};
    const ensure = (map, seg, fullKey) => {
        if (!map[seg]) map[seg] = { key: fullKey, seg, children: {}, isLeaf: false };
        return map[seg];
    };
    for (const [path, accionesSet] of hojas) {
        const segs = path.split('.');
        let map = rootMap;
        let acc = '';
        for (let i = 0; i < segs.length; i++) {
            acc = acc ? `${acc}.${segs[i]}` : segs[i];
            const node = ensure(map, segs[i], acc);
            if (i === segs.length - 1) {
                node.isLeaf = true;
                node.path = path;
                node.acciones = ordenarAcciones([...accionesSet]);
            }
            map = node.children;
        }
    }

    // Convertir a array con label/count/children
    const convertir = (map) => {
        const arr = Object.values(map).map((node) => {
            const children = convertir(node.children);
            const tieneHijos = children.length > 0;
            let count;
            if (node.isLeaf && !tieneHijos) {
                count = node.acciones.length
                    ? node.acciones.reduce((s, a) => s + (countByClave[`${node.path}.${a}`] || 0), 0)
                    : (countByClave[node.path] || 0);
            } else {
                count = children.reduce((s, c) => s + (c.count || 0), 0);
            }
            return {
                key: node.key,
                label: etiqueta(node.seg),
                isLeaf: node.isLeaf && !tieneHijos,
                isSpecial: false,
                acciones: node.acciones || [],
                path: node.path,
                count,
                children: tieneHijos ? ordenarHijos(children) : undefined,
            };
        });
        return arr;
    };
    let roots = convertir(rootMap);

    // ringover (plana) → bajo módulo sintético "Estadísticas"
    const idxRing = roots.findIndex((n) => n.key === 'ringover');
    if (idxRing !== -1) {
        const ring = roots.splice(idxRing, 1)[0];
        roots.push({
            key: 'estadisticas', label: etiqueta('estadisticas'), isLeaf: false, isSpecial: false,
            acciones: [], count: ring.count, children: [ring],
        });
    }

    // Nodos ESPECIALES (paneles): primer hijo de su módulo `bajo`, si el módulo existe y —cuando
    // tienen `gate`— su clave está en el catálogo. Generaliza el antiguo caso único de inventario.
    const clavesCatalogo = new Set(configuracion.map((p) => p.clave));
    for (const [key, def] of Object.entries(NODOS_ESPECIALES)) {
        if (def.gate && !clavesCatalogo.has(def.gate)) continue;
        const padre = roots.find((n) => n.key === def.bajo);
        if (!padre) continue;
        const especial = {
            key, label: def.label, isLeaf: true, isSpecial: true,
            acciones: [], path: undefined, count: null, children: undefined,
        };
        padre.children = [especial, ...(padre.children || [])]; // especial SIEMPRE primero
    }

    return ordenarHijos(roots);
}

// Aplana las hojas del árbol (para resolver el nodo seleccionado).
function buscarHoja(nodos, clave) {
    for (const n of nodos || []) {
        if (n.isLeaf && !n.isSpecial) {
            const claves = n.acciones.length ? n.acciones.map((a) => `${n.path}.${a}`) : [n.path];
            if (claves.includes(clave)) return n;
        }
        const hit = buscarHoja(n.children, clave);
        if (hit) return hit;
    }
    return null;
}

// ¿el subárbol matchea la búsqueda?
function nodoMatchea(n, q) {
    if (n.label.toLowerCase().includes(q)) return true;
    if (n.path && n.path.toLowerCase().includes(q)) return true;
    if (n.key && n.key.toLowerCase().includes(q)) return true;
    return (n.children || []).some((c) => nodoMatchea(c, q));
}

const GestorPermisos = () => {
    const [configuracion, setConfiguracion] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [unidadesNegocio, setUnidadesNegocio] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);

    const [permisoSeleccionado, setPermisoSeleccionado] = useState(null); // clave completa
    const [accionSeleccionada, setAccionSeleccionada] = useState(null);
    const [nodosExpandidos, setNodosExpandidos] = useState(new Set());
    const [busquedaArbol, setBusquedaArbol] = useState('');

    const [detalle, setDetalle] = useState(null); // { permiso, rolesDisponibles } derivado de configuracion

    const [rolesSeleccionados, setRolesSeleccionados] = useState(new Set());
    const [usuariosSeleccionados, setUsuariosSeleccionados] = useState(new Set());
    const [busquedaUsuarios, setBusquedaUsuarios] = useState("");
    const [filtroNegocio, setFiltroNegocio] = useState(VALOR_TODOS);
    const [filtroDepartamento, setFiltroDepartamento] = useState(VALOR_TODOS);
    const [userTab, setUserTab] = useState('granted'); // 'granted' | 'available'

    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [hayCambios, setHayCambios] = useState(false);

    // Carga inicial (sin autoseleccionar nada).
    useEffect(() => {
        const init = async () => {
            try {
                const [cfg, users, emps, deps] = await Promise.all([
                    fetchConfiguracionPermisos(),
                    fetchUsuariosAsignables(),
                    fetchUnidadesNegocioFiltro(),
                    fetchDepartamentosFiltro(),
                ]);
                setConfiguracion(cfg);
                setUsuarios(users);
                setUnidadesNegocio(emps);
                setDepartamentos(deps);
            } catch (error) {
                console.error("Error al cargar:", error);
                toast.error('Error al cargar la configuración de permisos');
            } finally {
                setCargando(false);
            }
        };
        init();
    }, []);

    const arbol = useMemo(() => construirArbol(configuracion), [configuracion]);
    const nodoSeleccionado = useMemo(() => {
        if (!permisoSeleccionado || esNodoEspecial(permisoSeleccionado)) return null;
        return buscarHoja(arbol, permisoSeleccionado);
    }, [arbol, permisoSeleccionado]);

    // Detalle derivado de `configuracion` al cambiar el permiso (sin fetch).
    useEffect(() => {
        if (!permisoSeleccionado || esNodoEspecial(permisoSeleccionado)) {
            setDetalle(null);
            return;
        }
        const p = configuracion.find((x) => x.clave === permisoSeleccionado);
        if (!p) { setDetalle(null); return; }
        setDetalle({
            permiso: { Id: p.Id, clave: p.clave, nombre: p.nombre, descripcion: p.descripcion },
            rolesDisponibles: ROLES_DISPONIBLES,
        });
        setRolesSeleccionados(new Set(p.roles || []));
        setUsuariosSeleccionados(new Set((p.usuarios || []).map((u) => u.Id)));
        setHayCambios(false);    }, [permisoSeleccionado, configuracion]);

    const togglePath = (key) => {
        setNodosExpandidos((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const seleccionarHoja = (n) => {
        if (n.isSpecial) { setPermisoSeleccionado(n.key); setAccionSeleccionada(null); return; }
        if (n.acciones.length) {
            const a = n.acciones[0];
            setAccionSeleccionada(a);
            setPermisoSeleccionado(`${n.path}.${a}`);
        } else {
            setAccionSeleccionada(null);
            setPermisoSeleccionado(n.path);
        }
    };

    const cambiarAccion = (a) => {
        if (!nodoSeleccionado) return;
        setAccionSeleccionada(a);
        setPermisoSeleccionado(`${nodoSeleccionado.path}.${a}`);
    };

    const toggleRol = (rol) => {
        const next = new Set(rolesSeleccionados);
        if (next.has(rol)) next.delete(rol); else next.add(rol);
        setRolesSeleccionados(next);
        setHayCambios(true);    };

    const concederIndividual = (usuarioId) => {
        const next = new Set(usuariosSeleccionados);
        next.add(usuarioId);
        setUsuariosSeleccionados(next);
        setHayCambios(true);    };

    const quitarIndividual = (usuarioId) => {
        const next = new Set(usuariosSeleccionados);
        next.delete(usuarioId);
        setUsuariosSeleccionados(next);
        setHayCambios(true);    };

    const guardar = async () => {
        setGuardando(true);        try {
            await updatePermiso(permisoSeleccionado, {
                roles: [...rolesSeleccionados],
                usuarios: [...usuariosSeleccionados],
            });
            // Refrescar configuracion para mantener badges sincronizados.
            const cfg = await fetchConfiguracionPermisos();
            setConfiguracion(cfg);
            toast.success('Permisos guardados');
            setHayCambios(false);
        } catch (error) {
            toast.error('Error al guardar permisos');
        } finally {
            setGuardando(false);
        }
    };

    // Filtro AND: búsqueda + unidad de negocio + departamento (el "solo con
    // acceso" lo sustituyen las pestañas Con acceso / Sin acceso).
    const usuariosFiltrados = useMemo(() => {
        const q = busquedaUsuarios.toLowerCase().trim();
        const negocioId = filtroNegocio !== VALOR_TODOS ? parseInt(filtroNegocio, 10) : null;
        const depId = filtroDepartamento !== VALOR_TODOS ? parseInt(filtroDepartamento, 10) : null;

        return usuarios.filter(u => {
            if (q) {
                const matchTexto = (
                    u.nombre?.toLowerCase().includes(q) ||
                    u.apellidos?.toLowerCase().includes(q) ||
                    u.mail_empresa?.toLowerCase().includes(q) ||
                    u.rol?.toLowerCase().includes(q)
                );
                if (!matchTexto) return false;
            }
            if (negocioId !== null) {
                const matchNegocio = u.unidadesNegocio?.some(n => n.Id === negocioId);
                if (!matchNegocio) return false;
            }
            if (depId !== null) {
                const matchDep = u.departamentos?.some(d => d.Id === depId);
                if (!matchDep) return false;
            }
            return true;
        });
    }, [usuarios, busquedaUsuarios, filtroNegocio, filtroDepartamento]);

    // Roles activos (incluye Super Administrador implícito).
    const rolesActivosSet = useMemo(() => {
        const s = new Set(rolesSeleccionados);
        s.add('Super Administrador');
        return s;
    }, [rolesSeleccionados]);

    // Asignaciones CRUDAS de la clave `.manage` hermana (solo si la seleccionada
    // es una `.view`), para el badge "implícito por gestión". NO alimenta el
    // guardado: se lee de `configuracion`, nunca de los Sets seleccionados.
    const { manageRolesSet, manageUsuariosSet } = useMemo(() => {
        const vacio = { manageRolesSet: null, manageUsuariosSet: null };
        if (!permisoSeleccionado || !permisoSeleccionado.endsWith('.view')) return vacio;
        const base = permisoSeleccionado.slice(0, -'.view'.length);
        const hermana = configuracion.find((x) => x.clave === `${base}.manage`);
        if (!hermana) return vacio;
        return {
            manageRolesSet: new Set(hermana.roles || []),
            manageUsuariosSet: new Set((hermana.usuarios || []).map((u) => u.Id)),
        };
    }, [permisoSeleccionado, configuracion]);

    // Partición con/sin acceso (sobre los ya filtrados).
    const usuariosConAcceso = useMemo(
        () => usuariosFiltrados.filter(u => tieneAcceso(calcularOrigenAcceso(u, rolesActivosSet, usuariosSeleccionados, manageRolesSet, manageUsuariosSet))),
        [usuariosFiltrados, rolesActivosSet, usuariosSeleccionados, manageRolesSet, manageUsuariosSet]
    );
    const usuariosSinAcceso = useMemo(
        () => usuariosFiltrados.filter(u => !tieneAcceso(calcularOrigenAcceso(u, rolesActivosSet, usuariosSeleccionados, manageRolesSet, manageUsuariosSet))),
        [usuariosFiltrados, rolesActivosSet, usuariosSeleccionados, manageRolesSet, manageUsuariosSet]
    );
    const usuariosVisibles = userTab === 'granted' ? usuariosConAcceso : usuariosSinAcceso;

    return (
        <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">
            {/* HEADER */}
            <div className="mb-5">
                <h1 className="text-xl font-semibold text-slate-900 leading-tight">Gestión de permisos</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Define qué roles y qué usuarios concretos pueden acceder a cada sección.
                    El rol Super Administrador siempre tiene acceso a todo.
                </p>
            </div>

            {cargando ? <SkeletonGestorPermisos /> : (
            <div className="grid grid-cols-12 gap-4">
                {/* ÁRBOL */}
                <Card className="col-span-12 lg:col-span-3 h-fit">
                    <CardContent className="p-3">
                        <Input
                            placeholder="Buscar permiso…"
                            value={busquedaArbol}
                            onChange={e => setBusquedaArbol(e.target.value)}
                            prefix={<Search size={14} />}
                            className="mb-3"
                        />
                        <ArbolPermisos
                            nodos={arbol}
                            expandidos={nodosExpandidos}
                            onToggle={togglePath}
                            seleccion={permisoSeleccionado}
                            onSeleccionar={seleccionarHoja}
                            busqueda={busquedaArbol}
                        />
                    </CardContent>
                </Card>

                {/* PANEL DERECHO */}
                <div className="col-span-12 lg:col-span-9">
                    {esNodoEspecial(permisoSeleccionado) ? (
                        NODOS_ESPECIALES[permisoSeleccionado].render({ usuarios, unidadesNegocio, departamentos })
                    ) : permisoSeleccionado && detalle ? (
                        <>
                            {detalle.permiso?.descripcion && (
                                <div className="text-[11px] text-slate-500 mb-3 italic">{detalle.permiso.descripcion}</div>
                            )}

                            {/* Toggle de acciones (solo si la hoja tiene 2+) */}
                            {nodoSeleccionado?.acciones?.length > 1 && (
                                <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-md mb-3">
                                    {nodoSeleccionado.acciones.map(a => (
                                        <button
                                            key={a}
                                            onClick={() => cambiarAccion(a)}
                                            className={cn(
                                                'px-3 py-1 text-sm rounded transition-colors',
                                                accionSeleccionada === a ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-gray-600'
                                            )}
                                        >
                                            {ETIQUETAS[a] ?? a}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* ROLES CON ACCESO (píldoras) */}
                            <Card className="mb-4">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <ShieldCheck className="w-4 h-4 text-slate-600" />
                                            <h3 className="text-sm font-medium text-slate-900">Roles con acceso</h3>
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {rolesSeleccionados.size} de {ROLES_DISPONIBLES.length}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Los usuarios con estos roles tendrán acceso automáticamente.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {ROLES_DISPONIBLES.map(rol => {
                                            const esSuperAdmin = rol === 'Super Administrador';
                                            const activo = esSuperAdmin || rolesSeleccionados.has(rol);
                                            return (
                                                <button
                                                    key={rol}
                                                    type="button"
                                                    disabled={esSuperAdmin}
                                                    onClick={() => toggleRol(rol)}
                                                    className={cn(
                                                        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition',
                                                        activo
                                                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                            : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100',
                                                        esSuperAdmin && 'opacity-70 cursor-not-allowed'
                                                    )}
                                                    title={esSuperAdmin ? 'Super Administrador siempre tiene acceso' : undefined}
                                                >
                                                    {rol}
                                                    {esSuperAdmin && <Lock className="w-3 h-3" />}
                                                    {activo && !esSuperAdmin && <Check className="w-3 h-3" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* USUARIOS (pestañas Con acceso / Sin acceso) */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Users className="w-4 h-4 text-slate-600" />
                                            <h3 className="text-sm font-medium text-slate-900">Usuarios</h3>
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {usuariosConAcceso.length} con acceso · {usuariosSinAcceso.length} sin acceso
                                        </span>
                                    </div>

                                    {/* Pestañas */}
                                    <div className="flex gap-1 border-b border-gray-200 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setUserTab('granted')}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition',
                                                userTab === 'granted'
                                                    ? 'border-blue-600 text-blue-700'
                                                    : 'border-transparent text-gray-600 hover:text-gray-900'
                                            )}
                                        >
                                            <UserCheck className="w-4 h-4" />
                                            Con acceso
                                            <span className={cn(
                                                'text-xs px-1.5 py-0.5 rounded-full',
                                                userTab === 'granted' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                            )}>{usuariosConAcceso.length}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setUserTab('available')}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition',
                                                userTab === 'available'
                                                    ? 'border-blue-600 text-blue-700'
                                                    : 'border-transparent text-gray-600 hover:text-gray-900'
                                            )}
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Sin acceso
                                            <span className={cn(
                                                'text-xs px-1.5 py-0.5 rounded-full',
                                                userTab === 'available' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                            )}>{usuariosSinAcceso.length}</span>
                                        </button>
                                    </div>

                                    {/* Filtros: búsqueda + 2 selects */}
                                    <div className="space-y-2 mb-3">
                                        <Input
                                            placeholder="Buscar usuario…"
                                            value={busquedaUsuarios}
                                            onChange={e => setBusquedaUsuarios(e.target.value)}
                                            prefix={<Search size={14} />}
                                            suffix={busquedaUsuarios && (
                                                <button onClick={() => setBusquedaUsuarios('')}
                                                    className="p-0.5 text-slate-400 hover:text-slate-700 transition-colors"
                                                    aria-label="Limpiar búsqueda" type="button">
                                                    <X size={14} />
                                                </button>
                                            )}
                                        />
                                        <div className="grid grid-cols-2 gap-2">
                                            <Select value={filtroNegocio} onChange={(e) => setFiltroNegocio(e.target.value)}
                                                prefix={<Building size={13} />} className="text-xs">
                                                <option value={VALOR_TODOS}>Todas las unidades de negocio</option>
                                                {unidadesNegocio.map(e => <option key={e.Id} value={e.Id}>{e.nombre}</option>)}
                                            </Select>
                                            <Select value={filtroDepartamento} onChange={(e) => setFiltroDepartamento(e.target.value)}
                                                prefix={<Network size={13} />} className="text-xs">
                                                <option value={VALOR_TODOS}>Todos los departamentos</option>
                                                {departamentos.map(d => <option key={d.Id} value={d.Id}>{d.nombre}</option>)}
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Lista de la pestaña activa */}
                                    <div className="max-h-96 overflow-y-auto -mx-1">
                                        {usuariosVisibles.length === 0 ? (
                                            <div className="py-10 text-center text-sm text-gray-500">
                                                {userTab === 'granted'
                                                    ? 'Ningún usuario tiene acceso a este permiso todavía.'
                                                    : 'Todos los usuarios visibles tienen acceso.'}
                                            </div>
                                        ) : (
                                            usuariosVisibles.map(u => (
                                                <FilaUsuario
                                                    key={u.Id}
                                                    usuario={u}
                                                    origen={calcularOrigenAcceso(u, rolesActivosSet, usuariosSeleccionados, manageRolesSet, manageUsuariosSet)}
                                                    tab={userTab}
                                                    onAdd={() => concederIndividual(u.Id)}
                                                    onRemove={() => quitarIndividual(u.Id)}
                                                />
                                            ))
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* GUARDAR */}
                            <div className="mt-5 flex justify-end items-center gap-3">
                                {hayCambios && <span className="text-xs text-amber-600 italic">Hay cambios sin guardar</span>}
                                <Button onClick={guardar} disabled={!hayCambios || guardando}>
                                    {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                                    {guardando ? 'Guardando…' : 'Guardar cambios'}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Card><CardContent className="py-16 text-center text-gray-500">
                            <p className="text-sm">Selecciona un permiso del árbol para configurarlo.</p>
                        </CardContent></Card>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

// ── Árbol recursivo ──
function ArbolPermisos({ nodos, expandidos, onToggle, seleccion, onSeleccionar, busqueda, nivel = 0 }) {
    const q = busqueda.trim().toLowerCase();
    const visibles = q ? (nodos || []).filter((n) => nodoMatchea(n, q)) : (nodos || []);

    if (nivel === 0 && q && visibles.length === 0) {
        return <div className="px-2 py-3 text-sm text-slate-400 italic">Sin resultados</div>;
    }

    return (
        <div className={nivel === 0 ? '' : ''}>
            {visibles.map((n) => (
                <FilaNodo
                    key={n.key}
                    nodo={n}
                    expandidos={expandidos}
                    onToggle={onToggle}
                    seleccion={seleccion}
                    onSeleccionar={onSeleccionar}
                    busqueda={busqueda}
                    nivel={nivel}
                />
            ))}
        </div>
    );
}

function FilaNodo({ nodo, expandidos, onToggle, seleccion, onSeleccionar, busqueda, nivel }) {
    const q = busqueda.trim().toLowerCase();
    const tieneHijos = !!nodo.children;
    const expandido = q ? true : expandidos.has(nodo.key);

    const seleccionado = nodo.isSpecial
        ? nodo.key === seleccion
        : (nodo.isLeaf && (
            nodo.acciones.length
                ? nodo.acciones.some((a) => `${nodo.path}.${a}` === seleccion)
                : nodo.path === seleccion
        ));

    const onClick = () => {
        if (tieneHijos) onToggle(nodo.key);
        else onSeleccionar(nodo);
    };

    return (
        <div>
            <div
                onClick={onClick}
                style={{ paddingLeft: 6 + nivel * 16 }}
                className={cn(
                    "flex items-center gap-1.5 pr-2 py-1.5 rounded-md text-sm cursor-pointer select-none transition-colors",
                    seleccionado ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700 hover:bg-slate-50"
                )}
            >
                {tieneHijos
                    ? (expandido ? <ChevronDown className="size-3.5 shrink-0 text-slate-400" /> : <ChevronRight className="size-3.5 shrink-0 text-slate-400" />)
                    : <span className="w-3.5 shrink-0" />}
                {nodo.isSpecial
                    ? <Settings2 className="size-3.5 shrink-0 text-slate-400" />
                    : tieneHijos
                        ? <Folder className="size-3.5 shrink-0 text-slate-400" />
                        : <File className="size-3.5 shrink-0 text-slate-400" />}
                <span className="flex-1 truncate">{nodo.label}</span>
                {nodo.count > 0 && (
                    <Badge variant="secondary" className="text-[10px]">{nodo.count}</Badge>
                )}
            </div>
            {tieneHijos && expandido && (
                <ArbolPermisos
                    nodos={nodo.children}
                    expandidos={expandidos}
                    onToggle={onToggle}
                    seleccion={seleccion}
                    onSeleccionar={onSeleccionar}
                    busqueda={busqueda}
                    nivel={nivel + 1}
                />
            )}
        </div>
    );
}

// ── Fila de usuario (lista de pestañas) ──
function FilaUsuario({ usuario, origen, tab, onAdd, onRemove }) {
    const nombreCompleto = `${usuario.nombre || ''} ${usuario.apellidos || ''}`.trim();
    return (
        <div className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50">
            <Avatar name={nombreCompleto} size="sm" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{nombreCompleto}</div>
                <div className="text-xs text-slate-500 truncate">
                    {usuario.rol || 'Sin rol'}{usuario.mail_empresa ? ` · ${usuario.mail_empresa}` : ''}
                </div>
            </div>
            <BadgeOrigen origen={origen} />
            <BotonAccion origen={origen} tab={tab} onAdd={onAdd} onRemove={onRemove} />
        </div>
    );
}

function BadgeOrigen({ origen }) {
    if (origen === 'super_admin') return <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Super Admin</Badge>;
    if (origen === 'rol') return <Badge className="bg-purple-50 text-purple-700 border border-purple-200">Por rol</Badge>;
    if (origen === 'individual') return <Badge className="bg-blue-50 text-blue-700 border border-blue-200">Individual</Badge>;
    if (origen === 'ambos') return <Badge className="bg-purple-50 text-purple-700 border border-purple-200">Rol + individual</Badge>;
    if (origen === 'implicito_gestion') return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Implícito por gestión</Badge>;
    return null;
}

function BotonAccion({ origen, tab, onAdd, onRemove }) {
    if (tab === 'available') {
        return (
            <button type="button" onClick={onAdd}
                className="p-1.5 rounded-full text-gray-400 hover:bg-green-50 hover:text-green-700 transition-colors"
                title="Conceder acceso individual">
                <Plus className="w-4 h-4" />
            </button>
        );
    }
    if (origen === 'super_admin') {
        return (
            <button type="button" disabled className="p-1.5 rounded-full text-gray-300 cursor-not-allowed"
                title="Super Administrador siempre tiene acceso">
                <Lock className="w-4 h-4" />
            </button>
        );
    }
    if (origen === 'rol') {
        return (
            <button type="button" disabled className="p-1.5 rounded-full text-gray-300 cursor-not-allowed"
                title="El acceso viene del rol. Quítalo desde la lista de roles arriba.">
                <Lock className="w-4 h-4" />
            </button>
        );
    }
    if (origen === 'implicito_gestion') {
        return (
            <button type="button" disabled className="p-1.5 rounded-full text-gray-300 cursor-not-allowed"
                title="El acceso a Ver es implícito: viene de tener Gestionar. Se quita retirando Gestionar en su clave.">
                <Lock className="w-4 h-4" />
            </button>
        );
    }
    // 'individual' o 'ambos'
    return (
        <button type="button" onClick={onRemove}
            className="p-1.5 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-700 transition-colors"
            title={origen === 'ambos'
                ? 'Quitar acceso individual (seguirá teniendo acceso por rol)'
                : 'Quitar acceso individual'}>
            <X className="w-4 h-4" />
        </button>
    );
}

// ── Skeleton de carga inicial (solo primer load) ──
function SkeletonGestorPermisos() {
    return (
        <div className="grid grid-cols-12 gap-4">
            <Card className="col-span-12 lg:col-span-3">
                <CardContent className="p-3">
                    <div className="h-9 bg-gray-100 rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className="h-7 bg-gray-100 rounded animate-pulse"
                                style={{ marginLeft: `${(i % 3) * 12}px`, width: `${70 + (i * 7) % 25}%` }}
                            />
                        ))}
                    </div>
                </CardContent>
            </Card>
            <div className="col-span-12 lg:col-span-9 space-y-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="h-5 w-40 bg-gray-100 rounded animate-pulse mb-3" />
                        <div className="flex flex-wrap gap-2">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="h-7 w-28 bg-gray-100 rounded-full animate-pulse" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="h-5 w-32 bg-gray-100 rounded animate-pulse mb-3" />
                        <div className="h-9 bg-gray-100 rounded animate-pulse mb-3" />
                        <div className="space-y-2">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default GestorPermisos;
